import { Table, Tooltip, Card } from "antd";
import type { ColumnsType } from "antd/es/table";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Icon } from "Components";
import { STATUS_COLOR } from "Pages/Dashboard/Dashboard.constants";
import { useTranslation } from "react-i18next";

type PropertyData = {
    key?: string;
    propertyName: string;
    status: "Compliant" | "Non-Compliant" | "Not Started";
    dueDate: string | "—";
    lastChange: string | "—";
};

type SortColumn = "name" | "status" | "dueDate" | "lastChange";

type PropertyComplianceTableProps = {
    properties: PropertyData[];
    sortColumn: string | null;
    sortDirection: "asc" | "desc";
    onSort: (column: SortColumn) => void;
};

const parseDate = (dateStr: string | "—"): Date | null => {
    if (dateStr === "—" || !dateStr) {
        return null;
    }
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null;
        return date;
    } catch {
        return null;
    }
};

export function PropertyComplianceTable({
    properties,
    sortColumn,
    sortDirection,
    onSort,
}: PropertyComplianceTableProps) {
    const { t } = useTranslation();

    const getSortIcon = (column: string) => {
        if (sortColumn !== column) {
            return <Icon type="chevronUpDown" />;
        }
        return sortDirection === "asc" ? (
            <Icon type="chevronUp" />
        ) : (
            <Icon type="chevronDown" className="w-4 h-4 text-foreground" />
        );
    };

    const StatusBadge = ({ status }: { status: PropertyData["status"] }) => {
        const { color, background } = STATUS_COLOR[status];
        const tagValue = t(`newDashboard.summaryTable.tags.${status}`)
        return (
            <span className={`status-tag`} style={{ whiteSpace: "nowrap", background, color }}>
                {tagValue}
            </span>
        );
    };

    const columns: ColumnsType<PropertyData> = [
        {
            title: (
                <span className="d-flex items-center gap-2">
                    {t("pciCompliance.propertyName")}
                    {getSortIcon("name")}
                </span>
            ),
            dataIndex: "propertyName",
            sorter: (a, b) => a.propertyName.localeCompare(b.propertyName),
            onHeaderCell: () => ({
                onClick: () => onSort("name"),
                style: {
                    cursor: "pointer",
                    width: "46rem",
                    maxWidth: "46rem",
                    paddingRight: "0px",
                    paddingLeft: "0px!important",
                },
            }),
        },
        {
            title: (
                <span className="d-flex items-center justify-end gap-2">
                    <span className="d-flex items-center justify-end gapx-6">
                        {t("pciCompliance.status")}
                        <Tooltip title={t("pciCompliance.statusTooltip")}>
                            <InfoCircleOutlined className="cursor-help text-gray-400" />
                        </Tooltip>
                    </span>
                    {getSortIcon("status")}
                </span>
            ),
            dataIndex: "status",
            align: "center",
            width: "11rem",
            sorter: (a, b) => a.status.localeCompare(b.status),
            onHeaderCell: () => ({
                width: "11rem",
                onClick: () => onSort("status"),
                style: {
                    cursor: "pointer",
                    width: "11rem",
                    maxWidth: "11rem",
                    paddingRight: "0px",
                    whiteSpace: "nowrap",
                },
            }),
            onCell: () => ({
                className: "text-right d-flex justify-center items-center px-8",
            }),
            render: (value) => (
                <div className="d-flex justify-end items-center rounded-sm">
                    <StatusBadge status={value} />
                </div>
            ),
        },
        {
            title: (
                <span className="d-flex items-center justify-end gap-2">
                    {t("pciCompliance.dueDate")}
                    {getSortIcon("dueDate")}
                </span>
            ),
            dataIndex: "dueDate",
            align: "right",
            sorter: (a, b) => {
                const dateA = parseDate(a.dueDate);
                const dateB = parseDate(b.dueDate);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA.getTime() - dateB.getTime();
            },
            onCell: () => ({ className: "px-8" }),
            onHeaderCell: () => ({
                width: "11rem",
                onClick: () => onSort("dueDate"),
                style: {
                    cursor: "pointer",
                    width: "11rem",
                    maxWidth: "11rem",
                    paddingRight: "0px",
                    whiteSpace: "nowrap",
                },
            }),
            render: (value) => (
                <div className="d-flex justify-end items-center">{value}</div>
            ),
        },
        {
            title: (
                <span className="d-flex items-center justify-end gap-2">
                    {t("pciCompliance.lastChange")}
                    {getSortIcon("lastChange")}
                </span>
            ),
            dataIndex: "lastChange",
            align: "right",
            sorter: (a, b) => {
                const dateA = parseDate(a.lastChange);
                const dateB = parseDate(b.lastChange);
                if (!dateA && !dateB) return 0;
                if (!dateA) return 1;
                if (!dateB) return -1;
                return dateA.getTime() - dateB.getTime();
            },
            onHeaderCell: () => ({
                onClick: () => onSort("lastChange"),
                style: {
                    cursor: "pointer",
                    width: "11rem",
                    maxWidth: "11rem",
                    paddingRight: "0px",
                    whiteSpace: "nowrap",
                },
            }),
            render: (value) => (
                <div className="d-flex justify-end items-center">{value}</div>
            ),
        },
    ];

    const dataSource = properties.map((p, i) => ({
        ...p,
        key: String(i),
    }));

    return (
        <>
            <div className="md:hidden space-y-3">
                {properties.map((property, index) => (
                    <Card key={index} className="dashboard-card mbx-12">
                        <div className="d-flex flex-col gap-2">
                            <p className="text-foreground fs-1 mb-0 fs-400">
                                {property.propertyName}
                            </p>
                            <div>
                                <StatusBadge status={property.status} />
                            </div>
                            <div className="d-flex justify-between fsx-14 text-muted-foreground">
                                <p className="mb-0">{t("pciCompliance.due")} {property.dueDate}</p>
                                <p className="mb-0">
                                    {t("pciCompliance.changed")} {property.lastChange || "—"}
                                </p>
                            </div>
                        </div>
                    </Card>
                ))}
            </div>

            <div className="hidden md:block">
                <Card className="p-0 card-table overflow-hidden shadow-sm border border-border/60">
                    <Table
                        className="compliance-table"
                        dataSource={dataSource}
                        columns={columns}
                        pagination={false}
                        sortDirections={["ascend", "descend", "ascend"]}
                        showSorterTooltip={false}
                        rowClassName={(_, index) =>
                            index % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                        }
                    />
                </Card>
            </div>
        </>
    );
}